// src/pages/ProductsPage.js
import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Sidebar from '../components/Sidebar';
import MarketPricePanel from '../components/MarketPricePanel';

const BASE_URL = (process.env.REACT_APP_API_URL || '/api').replace(/\/api$/, '');
const EMPTY_FORM = { storeId: '', name: '', description: '', price: '', imageUrl: '', tags: [] };

const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ width: 15, height: 15 }}>
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);
const EditIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);
const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
    <path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
  </svg>
);
const BoxIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
    <polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>
  </svg>
);
const CameraIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16 }}>
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
    <circle cx="12" cy="13" r="4"/>
  </svg>
);
const SparkleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16 }}>
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
  </svg>
);
const PencilIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16 }}>
    <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/>
  </svg>
);
const GalleryIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16 }}>
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
    <circle cx="8.5" cy="8.5" r="1.5"/>
    <polyline points="21 15 16 10 5 21"/>
  </svg>
);
const TagIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
    <line x1="7" y1="7" x2="7.01" y2="7"/>
  </svg>
);
const RefreshIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
    <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
  </svg>
);

const ALWAYS_VISIBLE_RADIUS = 99999;
const randomCode = () => Math.random().toString(36).substring(2, 8).toUpperCase();
const toLocalDate = (date) => new Date(date - date.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const toLocalDT = (date) => new Date(date - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
const EMPTY_COUPON = { title: '', description: '', discountType: 'FLAT', discountValue: '', capType: 'FLAT', code: '', maxRedemptions: '', validFrom: '', validUntil: '' };

const DISCOUNT_TYPES = [
  { value: 'FLAT',         label: 'Fixed Amount (₹)' },
  { value: 'PERCENTAGE',   label: 'Percentage (%)' },
  { value: 'INTENT_BASED', label: 'Intent-Based' },
];
const discountValueLabel = (type) => type === 'INTENT_BASED' ? 'Maximum Discount Cap' : 'Discount Value';
const discountPrefix = (type, value) =>
  type === 'FLAT' ? `₹${value}` : type === 'PERCENTAGE' ? `${value}%` : `up to ₹${value}`;

export default function ProductsPage() {
  const { user } = useAuth();
  const location = useLocation();
  const pendingOpenNew = useRef(!!location.state?.openNew);
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [fillMode, setFillMode] = useState(null);
  const [triggered, setTriggered] = useState(false);
  const [defaultMode] = useState(() => localStorage.getItem('sho_fill_mode') || 'photo');
  const [error, setError] = useState('');
  const photoInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const [couponProduct, setCouponProduct] = useState(null);
  const [couponForm, setCouponForm] = useState(EMPTY_COUPON);
  const [couponSaving, setCouponSaving] = useState(false);
  const [couponError, setCouponError] = useState('');
  const [showCouponAdvanced, setShowCouponAdvanced] = useState(false);
  const [showCouponTime, setShowCouponTime] = useState(false);
  const [openActionMenu, setOpenActionMenu] = useState(null);

  const openCoupon = (product) => {
    const now = new Date();
    const week = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    setCouponForm({ ...EMPTY_COUPON, code: randomCode(), validFrom: toLocalDate(now), validUntil: toLocalDate(week) });
    setCouponError('');
    setShowCouponAdvanced(false);
    setShowCouponTime(false);
    setCouponProduct(product);
  };

  const handleCoupon = e => setCouponForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const toggleCouponTime = (on) => {
    setShowCouponTime(on);
    setCouponForm(f => ({
      ...f,
      validFrom: on ? `${f.validFrom}T00:00` : f.validFrom.slice(0, 10),
      validUntil: on ? `${f.validUntil}T23:59` : f.validUntil.slice(0, 10),
    }));
  };

  const saveCoupon = async e => {
    e.preventDefault();
    setCouponSaving(true); setCouponError('');
    try {
      const isIntent = couponForm.discountType === 'INTENT_BASED';
      const prefix = isIntent
        ? `up to ${couponForm.capType === 'PERCENTAGE' ? `${couponForm.discountValue}%` : `₹${couponForm.discountValue}`}`
        : discountPrefix(couponForm.discountType, couponForm.discountValue);
      const autoTitle = couponForm.title.trim() || `${prefix} off ${couponProduct.name}`;
      const validFrom = showCouponTime ? couponForm.validFrom : `${couponForm.validFrom}T00:00`;
      const validUntil = showCouponTime ? couponForm.validUntil : `${couponForm.validUntil}T23:59`;
      await api.createCoupon({
        ...couponForm,
        title: autoTitle,
        validFrom,
        validUntil,
        storeId: couponProduct.storeId,
        productId: couponProduct.id,
        discountValue: parseFloat(couponForm.discountValue),
        ...(isIntent ? { capType: couponForm.capType } : {}),
        maxRedemptions: couponForm.maxRedemptions ? parseInt(couponForm.maxRedemptions) : undefined,
        triggerType: isIntent ? 'proximity' : 'always',
        triggerRadiusM: isIntent ? 100 : ALWAYS_VISIBLE_RADIUS,
      });
      setCouponProduct(null);
    } catch (err) { setCouponError(err.message); }
    finally { setCouponSaving(false); }
  };

  const selectMode = (mode) => {
    setFillMode(mode);
    setTriggered(false);
    localStorage.setItem('sho_fill_mode', mode);
    if (mode === 'photo') photoInputRef.current?.click();
    else if (mode === 'gallery') galleryInputRef.current?.click();
  };

  useEffect(() => {
    api.getStores().then(data => {
      const owned = data.filter(s => s.ownerId === user?.storeOwner?.id);
      setStores(owned);
      if (owned.length > 0) setSelectedStore(owned[0].id);
    });
  }, [user]);

  useEffect(() => {
    if (!selectedStore) return;
    setLoading(true);
    api.getProducts(selectedStore).then(setProducts).finally(() => setLoading(false));
    if (pendingOpenNew.current) {
      pendingOpenNew.current = false;
      openNew();
    }
  }, [selectedStore]);

  const openNew = () => { setForm({ ...EMPTY_FORM, storeId: selectedStore }); setError(''); setFillMode(null); setTriggered(false); setModal('new'); };
  const openEdit = (p) => { setForm({ storeId: p.storeId, name: p.name, description: p.description ?? '', price: p.price, imageUrl: p.imageUrl ?? '', tags: p.tags || [] }); setError(''); setFillMode(null); setModal(p); };
  const closeModal = () => setModal(null);

  const handle = e => setForm({ ...form, [e.target.name]: e.target.value });

  const handlePhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAnalyzing(true); setError('');
    try {
      const data = new FormData();
      data.append('image', file);
      const token = localStorage.getItem('sho_token');
      const res = await fetch(`${BASE_URL}/api/upload/analyze`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: data,
      });
      if (!res.ok) throw new Error('Analysis failed');
      const result = await res.json();
      setForm(f => ({
        ...f,
        name: result.name || f.name,
        description: result.description || f.description,
        imageUrl: result.imageUrl || f.imageUrl,
        tags: result.tags?.length ? result.tags : f.tags,
      }));
    } catch {
      setError('Could not analyze image. Fill in details manually.');
    } finally {
      setAnalyzing(false);
      setTriggered(true);
    }
  };

  const handleSuggest = async () => {
    if (!form.name.trim()) return;
    setSuggesting(true); setError('');
    try {
      const token = localStorage.getItem('sho_token');
      const res = await fetch(`${BASE_URL}/api/upload/suggest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: form.name }),
      });
      if (!res.ok) throw new Error('Suggestion failed');
      const result = await res.json();
      setForm(f => ({
        ...f,
        description: result.description || f.description,
        price: result.price || f.price,
        imageUrl: result.imageUrl || f.imageUrl,
        tags: result.tags?.length ? result.tags : f.tags,
      }));
    } catch {
      setError('Could not generate suggestion. Fill in details manually.');
    } finally {
      setSuggesting(false);
      setTriggered(true);
    }
  };

  const save = async e => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      let tags = form.tags;
      if (!tags?.length) {
        try {
          const token = localStorage.getItem('sho_token');
          const res = await fetch(`${BASE_URL}/api/upload/tags`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ name: form.name, description: form.description, price: form.price }),
          });
          if (res.ok) { const r = await res.json(); tags = r.tags || []; }
        } catch { /* non-fatal — save without tags */ }
      }
      const payload = { ...form, price: parseFloat(form.price), tags };
      if (modal === 'new') await api.createProduct(payload);
      else await api.updateProduct(modal.id, payload);
      setProducts(await api.getProducts(selectedStore));
      closeModal();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this product?')) return;
    await api.deleteProduct(id);
    setProducts(products.filter(p => p.id !== id));
  };

  return (
    <div className="dash-root">
      <Sidebar active="products" />
      <main className="dash-main">
        <div className="page-content">
          <div className="page-header">
            <div>
              <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', fontWeight: 700, letterSpacing: '-0.5px' }}>
                Products
              </h1>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginTop: 2 }}>
                {products.length} product{products.length !== 1 ? 's' : ''}
              </p>
            </div>
            <button className="btn btn-primary" onClick={openNew} disabled={!selectedStore}>
              <PlusIcon /> Add Product
            </button>
          </div>

          {/* Store tabs */}
          {stores.length > 1 && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
              {stores.map(s => (
                <button
                  key={s.id}
                  onClick={() => setSelectedStore(s.id)}
                  style={{
                    padding: '6px 14px',
                    border: '1px solid',
                    borderColor: selectedStore === s.id ? 'var(--primary)' : 'var(--border)',
                    background: selectedStore === s.id ? 'var(--primary-light)' : 'var(--surface)',
                    color: selectedStore === s.id ? 'var(--primary)' : 'var(--text-secondary)',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  {s.name}
                </button>
              ))}
            </div>
          )}

          {loading ? (
            <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}><div className="spinner" /></div>
          ) : products.length === 0 ? (
            <div className="table-card">
              <div className="empty-state">
                <BoxIcon />
                <h3>No products yet</h3>
                <p>Add products to showcase what your store has to offer.</p>
                <button className="btn btn-primary" onClick={openNew}><PlusIcon /> Add Product</button>
              </div>
            </div>
          ) : (
            <>
            {/* Desktop table */}
            <div className="table-card products-desktop-table">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Price</th>
                    <th>Status</th>
                    <th style={{ width: 80 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {products.map(p => (
                    <tr key={p.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div className="product-thumb">
                            {p.imageUrl ? <img src={p.imageUrl} alt={p.name} /> : <BoxIcon />}
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{p.name}</div>
                            {p.description && (
                              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {p.description}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td style={{ fontWeight: 600 }}>₹{parseFloat(p.price).toLocaleString()}</td>
                      <td>
                        <span className={`badge ${p.status === 'ACTIVE' ? 'badge-green' : 'badge-gray'}`}>
                          {p.status === 'ACTIVE' ? 'Available' : p.status === 'ARCHIVED' ? 'Archived' : 'Draft'}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn btn-secondary" style={{ padding: '5px 8px' }} title="Create coupon" onClick={() => openCoupon(p)}>
                            <TagIcon />
                          </button>
                          <button className="btn btn-secondary" style={{ padding: '5px 8px' }} onClick={() => openEdit(p)}>
                            <EditIcon />
                          </button>
                          <button className="btn btn-ghost" style={{ padding: '5px 8px', color: 'var(--danger)' }} onClick={() => remove(p.id)}>
                            <TrashIcon />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile card list */}
            <div className="table-card products-mobile-list">
              {openActionMenu && <div style={{ position: 'fixed', inset: 0, zIndex: 9 }} onClick={() => setOpenActionMenu(null)} />}
              {products.map(p => (
                <div key={p.id} className="product-mobile-row">
                  <div className="product-thumb">
                    {p.imageUrl ? <img src={p.imageUrl} alt={p.name} /> : <BoxIcon />}
                  </div>
                  <div className="product-mobile-info">
                    <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{p.name}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                      <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>₹{parseFloat(p.price).toLocaleString()}</span>
                      <span className={`badge ${p.status === 'ACTIVE' ? 'badge-green' : 'badge-gray'}`}>
                        {p.status === 'ACTIVE' ? 'Available' : p.status === 'ARCHIVED' ? 'Archived' : 'Draft'}
                      </span>
                    </div>
                  </div>
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    <button
                      className="btn btn-secondary"
                      style={{ padding: '5px 10px', fontSize: '1.1rem', lineHeight: 1 }}
                      onClick={() => setOpenActionMenu(openActionMenu === p.id ? null : p.id)}
                    >⋮</button>
                    {openActionMenu === p.id && (
                      <div style={{
                        position: 'absolute', right: 0, top: '110%', zIndex: 10,
                        background: 'var(--surface)', border: '1px solid var(--border)',
                        borderRadius: 'var(--radius-sm)', boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                        minWidth: 160, overflow: 'hidden',
                      }}>
                        {[
                          { label: 'Create Coupon', icon: <TagIcon />, action: () => { openCoupon(p); setOpenActionMenu(null); } },
                          { label: 'Edit', icon: <EditIcon />, action: () => { openEdit(p); setOpenActionMenu(null); } },
                          { label: 'Delete', icon: <TrashIcon />, action: () => { remove(p.id); setOpenActionMenu(null); }, danger: true },
                        ].map(({ label, icon, action, danger }) => (
                          <button key={label} onClick={action} style={{
                            display: 'flex', alignItems: 'center', gap: 10,
                            width: '100%', padding: '10px 14px', border: 'none',
                            background: 'none', cursor: 'pointer', fontSize: '0.875rem',
                            color: danger ? 'var(--danger)' : 'var(--text)', fontFamily: 'inherit',
                            textAlign: 'left',
                          }}>
                            {icon}{label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            </>
          )}
        </div>
      </main>

      {modal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={e => e.stopPropagation()}>

            {/* Hidden file inputs — triggered programmatically on mode select */}
            <input ref={photoInputRef} type="file" accept="image/*" capture="environment" onChange={handlePhoto} disabled={analyzing} style={{ display: 'none' }} />
            <input ref={galleryInputRef} type="file" accept="image/*" onChange={handlePhoto} disabled={analyzing} style={{ display: 'none' }} />

            {/* Step 1: Mode selection (new products only) */}
            {modal === 'new' && fillMode === null && (
              <>
                <h2>Add Product</h2>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginBottom: 20 }}>
                  How would you like to add this product?
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  {[
                    { id: 'photo',   Icon: CameraIcon,  title: 'Take Photo',      desc: 'AI reads your photo and auto-fills details' },
                    { id: 'gallery', Icon: GalleryIcon, title: 'From Gallery',     desc: 'Pick an existing photo — AI auto-fills details' },
                    { id: 'name',    Icon: SparkleIcon, title: 'Suggest by Name',  desc: 'Type a name — AI fills description & price'  },
                    { id: 'manual',  Icon: PencilIcon,  title: 'Manual Entry',     desc: 'Fill in all product details yourself'          },
                  ].map(({ id, Icon, title, desc }) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => selectMode(id)}
                      style={{
                        border: '1px solid',
                        borderColor: defaultMode === id ? 'var(--primary)' : 'var(--border)',
                        background: defaultMode === id ? 'var(--primary-light)' : 'var(--surface)',
                        borderRadius: 'var(--radius)',
                        padding: '16px 12px',
                        cursor: 'pointer',
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 8,
                      }}
                    >
                      <div style={{ color: defaultMode === id ? 'var(--primary)' : 'var(--text-secondary)' }}>
                        <Icon />
                      </div>
                      <strong style={{ fontSize: '0.8rem', color: 'var(--text)' }}>{title}</strong>
                      <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', lineHeight: 1.4 }}>{desc}</span>
                    </button>
                  ))}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeModal}>Cancel</button>
                </div>
              </>
            )}

            {/* Step 2: Form */}
            {(modal !== 'new' || fillMode !== null) && (
              <form onSubmit={save}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                  <h2 style={{ margin: 0 }}>{modal === 'new' ? 'Add Product' : 'Edit Product'}</h2>
                  {modal === 'new' && (
                    <button type="button" className="btn btn-ghost" style={{ fontSize: '0.8rem' }} onClick={() => setFillMode(null)}>
                      ← Change
                    </button>
                  )}
                </div>

                {/* Photo / Gallery mode */}
                {(fillMode === 'photo' || fillMode === 'gallery') && (
                  <div className="form-group">
                    {form.imageUrl ? (
                      <>
                        <img src={form.imageUrl} alt="preview" style={{ width: '100%', height: 140, objectFit: 'cover', borderRadius: 'var(--radius-sm)', marginBottom: 8 }} />
                        <button type="button" disabled={analyzing} onClick={() => (fillMode === 'photo' ? photoInputRef : galleryInputRef).current?.click()} style={{
                          width: '100%', padding: '8px', fontSize: '0.8rem',
                          border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
                          background: 'var(--surface)', color: 'var(--text-secondary)',
                          cursor: 'pointer', fontFamily: 'inherit',
                        }}>
                          {analyzing ? 'Analyzing…' : 'Change Photo'}
                        </button>
                      </>
                    ) : (
                      <div style={{
                        border: '2px dashed var(--border)', borderRadius: 'var(--radius)',
                        padding: '32px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.875rem',
                      }}>
                        {analyzing ? 'Analyzing…' : 'Waiting for photo…'}
                      </div>
                    )}
                  </div>
                )}

                {/* Name mode */}
                {fillMode === 'name' && (
                  <div className="form-group">
                    <label>Product Name</label>
                    <input name="name" value={form.name} onChange={handle} required placeholder="e.g. Blue Denim Jacket" style={{ fontSize: '1rem' }} />
                  </div>
                )}

                {/* Fields revealed after trigger */}
                {(triggered || fillMode === 'manual' || modal !== 'new') && (
                  <>
                    {fillMode !== 'name' && (
                      <div className="form-group" style={{ marginTop: (fillMode === 'photo' || fillMode === 'gallery') ? 12 : 0 }}>
                        <label>Product Name</label>
                        <input name="name" value={form.name} onChange={handle} required placeholder="e.g. Blue Denim Jacket" />
                      </div>
                    )}
                    <div className="form-group" style={{ marginTop: 12 }}>
                      <label>Description</label>
                      <textarea name="description" value={form.description} onChange={handle} rows={2} />
                    </div>
                    <div className="form-group" style={{ marginTop: 12 }}>
                      <label>Price (₹)</label>
                      <input name="price" type="number" step="0.01" value={form.price} onChange={handle} required placeholder="999" />
                      <MarketPricePanel productName={form.name} />
                    </div>
                    {fillMode !== 'photo' && fillMode !== 'gallery' && (
                      <div className="form-group" style={{ marginTop: 12 }}>
                        <label>Product Photo</label>
                        <label style={{ cursor: 'pointer' }}>
                          <input type="file" accept="image/*" onChange={handlePhoto} disabled={analyzing} style={{ display: 'none' }} />
                          <div style={{
                            border: '2px dashed var(--border)',
                            borderRadius: 'var(--radius)',
                            padding: '16px',
                            textAlign: 'center',
                            color: 'var(--text-secondary)',
                            fontSize: '0.8rem',
                          }}>
                            {analyzing ? 'Analyzing…' : form.imageUrl ? 'Change Photo' : 'Upload Photo'}
                          </div>
                        </label>
                        {form.imageUrl && (
                          <img src={form.imageUrl} alt="preview" style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 'var(--radius-sm)', marginTop: 8 }} />
                        )}
                      </div>
                    )}
                  </>
                )}

                {error && <div className="alert alert-error" style={{ marginTop: 12 }}>{error}</div>}
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeModal}>Cancel</button>
                  {fillMode === 'name' && !triggered && modal === 'new' && (
                    <button type="button" className="btn btn-primary" onClick={handleSuggest} disabled={suggesting || !form.name.trim()}>
                      {suggesting ? 'Thinking…' : 'Suggest'}
                    </button>
                  )}
                  {(triggered || fillMode === 'manual' || modal !== 'new') && (
                    <button type="submit" className="btn btn-primary" disabled={saving}>
                      {saving ? 'Saving…' : 'Save Product'}
                    </button>
                  )}
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {couponProduct && (
        <div className="modal-overlay" onClick={() => setCouponProduct(null)}>
          <div className="modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
            <h2 style={{ marginBottom: 4 }}>Create Coupon</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginBottom: 20 }}>
              For <strong>{couponProduct.name}</strong>
            </p>
            <form onSubmit={saveCoupon}>
              <div className="form-grid">
                {/* Core fields */}
                <div className="form-group full" style={{ flexDirection: 'row', gap: 12 }}>
                  <div className="form-group" style={{ flex: 1, minWidth: 0 }}>
                    <label>Discount Type</label>
                    <select name="discountType" value={couponForm.discountType} onChange={handleCoupon}>
                      {DISCOUNT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ flex: 1, minWidth: 0 }}>
                    <label>{discountValueLabel(couponForm.discountType)}</label>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {couponForm.discountType === 'INTENT_BASED' && (
                        <select name="capType" value={couponForm.capType} onChange={handleCoupon} style={{ width: 64, flexShrink: 0 }}>
                          <option value="FLAT">₹</option>
                          <option value="PERCENTAGE">%</option>
                        </select>
                      )}
                      <input name="discountValue" type="number" step="0.01" value={couponForm.discountValue} onChange={handleCoupon} required
                        placeholder={
                          couponForm.discountType === 'PERCENTAGE' ? '30' :
                          couponForm.discountType === 'INTENT_BASED' && couponForm.capType === 'PERCENTAGE' ? '30' : '500'
                        } style={{ flex: 1, minWidth: 0 }} />
                    </div>
                  </div>
                </div>
                {couponForm.discountType === 'INTENT_BASED' && (
                  <div className="form-group full">
                    <span className="form-hint">Shoppr's algorithm determines the optimal discount for each shopper based on their behaviour and intent — you only set the cap.</span>
                  </div>
                )}
                <div className="form-group">
                  <label>Max Redemptions</label>
                  <input name="maxRedemptions" type="number" value={couponForm.maxRedemptions} onChange={handleCoupon} placeholder="Unlimited" />
                </div>
                <div className="form-group full" style={{ flexDirection: 'row', gap: 12 }}>
                  <div className="form-group" style={{ flex: 1, minWidth: 0 }}>
                    <label>Valid From</label>
                    <input name="validFrom" type={showCouponTime ? 'datetime-local' : 'date'} value={couponForm.validFrom} onChange={handleCoupon} required />
                  </div>
                  <div className="form-group" style={{ flex: 1, minWidth: 0 }}>
                    <label>Valid Until</label>
                    <input name="validUntil" type={showCouponTime ? 'datetime-local' : 'date'} value={couponForm.validUntil} onChange={handleCoupon} required />
                  </div>
                </div>
              </div>

              {/* Advanced settings toggle */}
              <button type="button" onClick={() => setShowCouponAdvanced(v => !v)} style={{
                display: 'flex', alignItems: 'center', gap: 6, marginTop: 16,
                background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                fontSize: '0.8rem', color: 'var(--text-secondary)', fontFamily: 'inherit',
              }}>
                <span style={{ fontSize: '0.7rem' }}>{showCouponAdvanced ? '▼' : '▶'}</span>
                Advanced settings
              </button>

              {showCouponAdvanced && (
                <div className="form-grid" style={{ marginTop: 12 }}>
                  <div className="form-group full">
                    <label>Title <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(auto-generated if blank)</span></label>
                    <input name="title" value={couponForm.title} onChange={handleCoupon} placeholder={`e.g. ₹${couponForm.discountValue || '…'} off ${couponProduct.name}`} />
                  </div>
                  <div className="form-group full">
                    <label>Description</label>
                    <input name="description" value={couponForm.description} onChange={handleCoupon} placeholder="e.g. Valid on this product only" />
                  </div>
                  <div className="form-group">
                    <label>Coupon Code</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input name="code" value={couponForm.code} onChange={handleCoupon} required style={{ flex: 1 }} />
                      <button type="button" className="btn btn-secondary" style={{ padding: '8px 12px' }} onClick={() => setCouponForm(f => ({ ...f, code: randomCode() }))}>
                        <RefreshIcon />
                      </button>
                    </div>
                  </div>
                  <div className="form-group full" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <input type="checkbox" id="showTime" checked={showCouponTime} onChange={e => toggleCouponTime(e.target.checked)} style={{ width: 'auto', margin: 0 }} />
                    <label htmlFor="showTime" style={{ margin: 0, cursor: 'pointer', fontWeight: 400 }}>Set specific times for valid from / until</label>
                  </div>
                </div>
              )}

              {couponError && <div className="alert alert-error" style={{ marginTop: 12 }}>{couponError}</div>}
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setCouponProduct(null)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={couponSaving}>
                  {couponSaving ? 'Creating…' : 'Create Coupon'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
